import React, { ChangeEvent, MouseEvent, KeyboardEvent, useEffect, useState } from 'react';
import { Container, Typography, Box, Paper, Checkbox, Table, TableCell, TableContainer, TableHead, TableRow, TablePagination, CircularProgress, TableBody, Avatar, Tooltip, Link, Stack, Button, IconButton } from '@mui/material';
import { Query, QueryDocumentSnapshot, QuerySnapshot, and, collection, doc, endBefore, getCountFromServer, getDocs, getFirestore, limit, limitToLast, or, orderBy, query, setDoc, startAfter, startAt, where, writeBatch } from 'firebase/firestore';
import { Employee } from '../../types/Employee';
import { Vacation } from '../../types/Vacation';
import employeeConverter from '../../converters/employeeConverter';
import { useAppDispatch, useAppSelector } from '../../store';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { setEmployeePerPage } from '../../store/slices/appSlice';
import { KeyboardReturnRounded, PersonRemoveRounded, PersonSearchRounded } from '@mui/icons-material';
import { CleanButton } from '../CleanButton';
import { useNavigate } from 'react-router-dom';
import vacationRequestConverter from '../../converters/vacationRequestConverter';
import { DateTime } from 'luxon';
import { Search, SearchIconWrapper, StyledInputBase } from '../Search';
import { useDebouncedCallback } from 'use-debounce';
import { SecureComponent } from '../../security/SecureComponent';

type Tobi = {
  e: Employee,
  v?: Vacation,
  p?: boolean,
};

export const EmployeesOnVacationPage = () => {
  const db = getFirestore();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const employeePerPage = useAppSelector((s) => s.app.employeePerPage);

  const [page, setPage] = useState(0);
  const [firstVisibleEmployee, setFirstVisibleEmployee] = useState<QueryDocumentSnapshot | null>(null);
  const [lastVisibleEmployee, setLastVisibleEmployee] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Tobi[]>([]);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const setEmployeeList = async (qSnapshot: QuerySnapshot<Employee>) => {
    await Promise.allSettled<Tobi>(qSnapshot.docs.map(async d => {
      const e = d.data();
      const vs = await getDocs(query(collection(db, `Employees/${e.idNumber}/Vacations`), where('status', '==', 'In Progress')).withConverter(vacationRequestConverter));
      if (vs.docs.length > 0)
        return {
          e, v: vs.docs[0].data(), p: false,
        };

      return {
        e, v: undefined, p: false,
      };
    })).then(rs => {
      return (rs as PromiseFulfilledResult<Tobi>[]).map(r => r.value);
    }).then(ts => {
      setEmployees(ts);
      setLoading(false);
      setFirstVisibleEmployee(qSnapshot.docs[0]);
      setLastVisibleEmployee(qSnapshot.docs[qSnapshot.docs.length - 1]);
    });
  };

  const handleEmployeeNameOnClick = (event: MouseEvent<HTMLButtonElement>, employee: any) => {
    event.stopPropagation();
    navigate(`/home/employees/${employee.e.idNumber}`, {
      state: {
        employee: employee.e,
      }
    });
  };

  const handlePageOnChange = (event: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    let q: Query<Employee> | null = null;
    if (to > page) {
      // Next
      q = query(collection(db, 'Employees'), where('status', '==', 'On Vacation'), orderBy('idNumber', 'asc'), startAfter(lastVisibleEmployee), limit(employeePerPage)).withConverter(employeeConverter);
    } else if (to < page) {
      // Prev
      q = query(collection(db, 'Employees'), where('status', '==', 'On Vacation'), orderBy('idNumber', 'asc'), endBefore(firstVisibleEmployee), limitToLast(employeePerPage)).withConverter(employeeConverter);
    } else {
      // Same page
      q = query(collection(db, 'Employees'), where('status', '==', 'On Vacation'), orderBy('idNumber', 'asc'), startAt(firstVisibleEmployee), limit(employeePerPage)).withConverter(employeeConverter);
    }
    
    q = query(
      q, 
      or(
        and(
          where('idNumber', '>=', search), 
          where('idNumber', '<=', search + '\uf8ff'),
        ),
        and(
          where('iqamaNumber', '==', search),
        ),
      )
    );

    getDocs(q).then((qSnapshot) => {
      setEmployeeList(qSnapshot).then(() => {
        setPage(to);
      });
    });
  };
  
  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setEmployeePerPage(+event.target.value));
    setPage(0);
  };

  const handleReturnOnClick = (t: Tobi) => {
    const c = employees.map(tt => {
      if (tt.e.uid === t.e.uid) {
        return { ...tt, p: true };
      }
      return tt;
    });
    setEmployees(c);

    const e = t.e;
    const v = t.v;
    if (v) {
      const batch = writeBatch(db);
      try {
        const eRef = doc(db, `Employees/${e.idNumber}`);
        const vRef = doc(db, `Employees/${e.idNumber}/Vacations/${v.id}`);

        batch.set(vRef, { status: 'Completed' }, { merge: true });
        batch.set(eRef, { status: 'Active' }, { merge: true });
        batch.commit().then(() => {
          handlePageOnChange(null, page);
        });
      } catch {

      }
    } else {
      const eRef = doc(db, `Employees/${e.idNumber}`);
      setDoc(eRef, { status: 'Active' }, { merge: true }).then(() => {
        handlePageOnChange(null, page);
      });
    }
  };

  const fill = (value: string) => {
    const constraints = and(
      where('status', '==', 'On Vacation'),
      or(
        and(
          where('idNumber', '>=', value), 
          where('idNumber', '<=', value + '\uf8ff'),
        ),
        and(
          where('iqamaNumber', '==', value),
        ),
      )
    );
    const a = query(
      collection(db, 'Employees'), 
      constraints,
      orderBy('idNumber', 'asc')
    ).withConverter(employeeConverter);
    const q = query(a, limit(employeePerPage));
    getDocs(q).then((qSnapshot) => setEmployeeList(qSnapshot));
    getCountFromServer(a).then(count => setTotalCount(count.data().count));
  };

  // Search functions
  const searchDebounced = useDebouncedCallback((value) => {
    fill(value);
  }, 1000);

  const handleSearchOnChanged = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearch(evt.currentTarget.value);
    searchDebounced(evt.currentTarget.value);
  };

  useEffect(() => {
    fill(search);
  }, [employeePerPage]);

  return (
    <Container disableGutters>
      <Typography variant="h5" mt={2} mb={5}>Employees - On Vacation</Typography>
      <Paper elevation={4} sx={{ width: '100%', overflow: 'hidden' }}>
        <Box p={1} display="flex" flexDirection="row">
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search on-vacation employee..."
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
        </Box>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width={76} align="right">Count</TableCell>
                <TableCell align="left">Employee</TableCell>
                <TableCell width={215} align="left">Vacation Start Date</TableCell>
                <TableCell width={215} align="left">Vacation End Date</TableCell>
                <SecureComponent roles={['editor.vacation']}>
                  <TableCell width={90} align="center">Action</TableCell>
                </SecureComponent>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell colSpan={6}>
                    <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      <CircularProgress />
                      <Typography variant='caption' sx={{ mt: 2 }}>Please wait...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {employees.map((e, i) => {
                return (
                  <TableRow hover key={e.e.uid}>
                    <TableCell component="th" scope="row" align="right">{(page * employeePerPage) + i + 1}</TableCell>
                    <TableCell align="left">
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}>
                        <Avatar alt={e.e.name} src={e.e.picture} sx={{ mr: 2 }} />
                        <Box id={e.e.idNumber}>
                          <Typography fontWeight={700}>
                            <CleanButton onClick={(evt) => handleEmployeeNameOnClick(evt, e)}>{e.e.name}</CleanButton>
                          </Typography>
                          <Typography variant="caption">{e.e.idNumber}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography>{e.v ? DateTime.fromFormat(e.v.requestDateStart, 'yyyyMMdd').toFormat('MMMM dd, yyyy') : ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{e.v ? DateTime.fromFormat(e.v.requestDateEnd, 'yyyyMMdd').toFormat('MMMM dd, yyyy') : ''}</Typography>
                    </TableCell>
                    <SecureComponent roles={['editor.vacation']}>
                      <TableCell align='center'>
                        <Tooltip title='Return from vacation'>
                          <span>
                            <IconButton disabled={e.p} color='primary' onClick={() => {
                              if (window.confirm(`Are you sure you want to 'Return' employee ${e.e.name} from vacation?\n\nPress OK to confirm.`) === true) {
                                handleReturnOnClick(e);
                              }
                            }}>
                              <KeyboardReturnRounded />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </SecureComponent>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[25, 50, 100]}
          colSpan={5}
          count={totalCount}
          page={page}
          rowsPerPage={employeePerPage}
          SelectProps={{
            inputProps: {
              'aria-label': 'rows per page',
            },
          }}
          onPageChange={handlePageOnChange}
          onRowsPerPageChange={handleRowsPerPageOnChange}
          ActionsComponent={TablePaginationActions} />
      </Paper>
    </Container>
  );
};